import { useAuthHeaders } from "@src/frontend/hooks/useAuthHeaders";
import { useMessageHandler } from "@src/frontend/hooks/useMessageHandler";
import type { Res } from "@src/frontend/utils/types";
import { useRequest } from "ahooks";
import { useListDataScore } from "../stores/listDataScore";
import { Api } from "@mui/icons-material";

export const useRestAPI = <Data>(API: string) => {
  const { handleMessage } = useMessageHandler();
  const { getAuthHeader } = useAuthHeaders();
  const listReq = useRequest<Res<Data[]> | null, []>(async () => {
    const headers = await getAuthHeader();
    if (!headers) return null;
    return fetch(API, { headers }).then(res => res.json())
      .then((data: Res<Data[]>) => {
        useListDataScore.getState().setMap(API, data.data ?? []);
        return handleMessage(data);
      }).catch(e => {
        handleMessage(e);
        return null;
      });
  }, { refreshDeps: [getAuthHeader, API], debounceWait: 300 });
  const createReq = useRequest(async (data: Omit<Partial<Data>, 'id' | 'logto_uid'>) => {
    const headers = await getAuthHeader();
    if (!headers) return;
    return fetch(API, {
      headers, method: 'POST', body: JSON.stringify({ ...data }),
    }).then(res => res.json())
      .then(data => {
        handleMessage(data);
        return data;
      }).catch(e => handleMessage(e));
  }, { manual: true, debounceMaxWait: 300, refreshDeps: [getAuthHeader, API]});
  const updateReq = useRequest(async (data: Omit<Partial<Data>, 'id' | 'logto_uid'>, id: string) => {
    const headers = await getAuthHeader();
    if (!headers) return;
    return fetch(API, {
      headers, method: 'PUT', body: JSON.stringify({ ...data, id }),
    }).then(res => res.json())
      .then(data => {
        handleMessage(data);
        return data;
      }).catch(e => handleMessage(e));
  }, { manual: true, debounceMaxWait: 300, refreshDeps: [getAuthHeader, API]});
  const delReq = useRequest(async (id: string) => {
    const headers = await getAuthHeader();
    if (!headers) return;
    fetch(API, { headers, method: 'DELETE', body: JSON.stringify({ id })})
      .then(res => res.json())
      .then(data => handleMessage(data))
      .catch(e => handleMessage);
  }, { manual: true, debounceWait: 300, refreshDeps: [getAuthHeader, API]})
  return {
    listReq, createReq, updateReq, delReq,
  }
}